"use client";
import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Checkbox } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/16/solid";
import Link from "next/link";
import { toast } from "react-hot-toast";
import { getProviders, signIn } from "next-auth/react";
import ModalLibrary from "./ModalLibrary";
import userService from "@/services/user-service";
import { redirectToRoleBasedRoute } from "@/utils/navigationUtils";
import { useAuth } from "@/hooks/useAuth";

const Registration = () => {
  const { login, user, isAuthenticated } = useAuth();

  const [inputValueEmail, setInputValueEmail] = useState("");
  const [inputValueGeslo, setInputValueGeslo] = useState("");
  const [inputValueConfirmGeslo, setInputValueConfirmGeslo] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [enableReceiveEmails, setEnableReceiveEmails] = useState(true);
  const [enableTermsOfUse, setEnableTermsOfUse] = useState(false);
  // "Zapomni si" (login only): on = the login lasts 90 days, off = about a day
  const [enabledRememberMe, setEnabledRememberMe] = useState(true);

  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth > 768) {
      setIsDesktop(true);
    }
  }, []);

  // Check if user is already logged in and redirect
  useEffect(() => {
    if (isAuthenticated) {
      if (user && user.role && user.slugKey) {
        redirectToRoleBasedRoute(user.role, user.slugKey, isDesktop);
      }
    }
  }, [user, isAuthenticated, isDesktop]);

  const [activeDiv, setActiveDiv] = useState("login");

  const handleEmailInput = (event) => {
    setInputValueEmail(event.target.value);
  };

  const handleGesloInput = (event) => {
    setInputValueGeslo(event.target.value);
  };

  // Registration: e-mail, a password the visitor chooses and the same password again. The visitor is
  // logged in straight away; the welcome e-mail repeats the password for later.
  const passwordsDiffer =
    inputValueConfirmGeslo.length > 0 && inputValueGeslo !== inputValueConfirmGeslo;

  const handleRegister = async () => {
    if (isRegistering) return;
    const email = inputValueEmail.trim();
    if (!email) {
      toast.error("Vnesi e-naslov");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Napaka pri vnosu e-naslova");
      return;
    }
    if (inputValueGeslo.length < 6) {
      toast.error("Geslo mora imeti vsaj 6 znakov");
      return;
    }
    if (inputValueGeslo !== inputValueConfirmGeslo) {
      toast.error("Gesli se ne ujemata");
      return;
    }
    if (!enableTermsOfUse) {
      toast.error("Za registracijo se moraš strinjati s splošnimi pogoji in politiko zasebnosti");
      return;
    }
    setIsRegistering(true);
    try {
      const response = await userService.register({
        email,
        password: inputValueGeslo,
        acceptTerms: true,
        receiveEmails: enableReceiveEmails,
      });
      if (response.error) {
        toast.error(response.error || "Registracija ni uspela. Poskusi znova.");
        return;
      }

      toast.success("Registracija je uspela. Potrdilo smo poslali na tvoj e-naslov.");
      // Same login as for everybody else; it also takes the visitor to their page.
      await login({ email, password: inputValueGeslo });
    } catch (error) {
      toast.error("Registracija ni uspela. Poskusi znova.");
    } finally {
      setIsRegistering(false);
    }
  };

  // useAuth().login shows the messages and takes the visitor to their page.
  const handleLogin = async () => {
    if (!inputValueEmail || !inputValueGeslo) {
      toast.error("Potrebna sta e-naslov in geslo");
      return;
    }
    await login({ email: inputValueEmail.trim(), password: inputValueGeslo }, { rememberMe: enabledRememberMe });
  };

  // Google / Facebook. They only work once the keys of that provider are set on the server.
  const handleSocialLogin = async (provider, label) => {
    const providers = await getProviders();
    if (!providers?.[provider]) {
      toast.error(`Prijava z ${label} trenutno ni na voljo`);
      return;
    }
    await signIn(provider, { callbackUrl: "/registracija" });
  };

  // The address a social sign-in came back to when it failed.
  useEffect(() => {
    const problem = new URLSearchParams(window.location.search).get("napaka");
    if (problem === "email") {
      toast.error("Ponudnik prijave nam ni posredoval e-naslova. Registriraj se z e-naslovom.");
    } else if (problem === "prijava") {
      toast.error("Prijava ni uspela. Poskusi znova.");
    }
  }, []);

  return (
    <div className="relative w-full min-h-screen overflow-hidden">
      <ModalLibrary
        isShowModal={showForgotPassword}
        setIsShowModal={setShowForgotPassword}
        select_id="2"
        set_Id={() => {}}
      />
      {/* Background Image - Full Screen */}
      <div
        className="fixed inset-0 w-full h-full z-0"
        style={{
          backgroundImage: "url('/user/main_background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      />

      {/* Content Container */}
      <form className="relative z-10 max-w-[1920px] min-h-screen mobile:pb-[10px] mobile:pt-[0px] w-full desktop:mt-[92.02px] mobile:mt-[0px] tablet:mt-[79px] flex flex-col justify-center items-center mx-auto">
        <div
          className="w-[550px] tablet:pt-[79px] tablet:pb-[54px] desktop:pt-[79px] desktop:pb-[54px] mobile:max-w-[340px] mobile:w-full mobile:mx-2 flex flex-col
       tablet:bg-gray-300/30 desktop:bg-gray-300/30 desktop:backdrop-blur tablet:backdrop-blur rounded-2xl border-[2px] border-[#FFFFFF] desktop:shadow-lg tablet:shadow-lg mobile:border-[0px]"
        >
          {/* register and Login buttons */}
          <div className="flex flex-col mx-auto items-center w-[401px] h-auto mobile:w-full">
            <div className="flex flex-row justify-between mx-auto">
              {/* Login Div */}
              <div
                className={`transition-all duration-300 ease-in-out ${activeDiv === "login"
                    ? "w-[234px] h-[56px] mobile:w-[180px] mobile:h-[48px] shadow-custom-dark-to-white bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF] rounded-[8px] border-[1px] border-[#FFFFFF] text-[#1E2125] text-[24px] mobile:text-[24px] leading-[24px] font-variation-customOpt24"
                    : "w-[143px] h-[36px] bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF] shadow-custom-light-dark-with-white rounded-[8px] text-[#6D778E] text-[16px] leading-[24px] font-variation-customOpt16"
                  }  flex justify-center items-center`}
              >
                <button
                  type="button"
                  onClick={() => setActiveDiv("login")}
                  className=" w-full  mx-2 h-full my-[2px]"
                >
                  PRIJAVA
                </button>
              </div>

              {/* Register Div */}
              <div
                className={`transition-all duration-300 ease-in-out ${activeDiv === "register"
                    ? "w-[234px] h-[56px] mobile:w-[180px] mobile:h-[48px] shadow-custom-dark-to-white bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF] rounded-[8px] border-[1px] border-[#FFFFFF] text-[#1E2125] text-[24px] mobile:text-[24px] leading-[24px] font-variation-customOpt24"
                    : "w-[143px] h-[36px] bg-gradient-to-br from-[#E3E8EC] to-[#FFFFFF] shadow-custom-light-dark-with-white rounded-[8px] text-[#6D778E] text-[16px] leading-[24px] font-variation-customOpt16"
                  } flex justify-center items-center ml-4`}
              >
                <button
                  type="button"
                  onClick={() => setActiveDiv("register")}
                  className="w-full  mx-2 h-full my-[2px]"
                >
                  REGISTRACIJA
                </button>
              </div>
            </div>
            <Image
              src={"/img_down_arrow.png"}
              width={20}
              height={12}
              alt="Down Arrow"
              className="mt-[14px]"
            />
          </div>

          {/* Container for text inputs and forgot password and checkbox */}
          <div className="w-[429px] h-auto mobile:w-[297px] mobile:h-auto mt-[10px] mx-auto flex flex-col ">
            {/* Container for email field */}
            <div className=" text-[#6D778E] text-[14px] leading-[20px] font-[400px] w-full mt-[10px] h-[62px] flex flex-col justify-start items-start">
              <div>E-pošta</div>
              <div className="px-[10px] mt-[4px] h-[38px] rounded-[6px] bg-[#F2F8FF66] shadow-custom-dark-to-white w-full">
                <input
                  type="email"
                  value={inputValueEmail}
                  autoComplete="email"
                  onChange={handleEmailInput}
                  className="w-full h-full bg-transparent focus:outline-none text-[#848484]"
                />
              </div>
            </div>
            {/* Container for geslo field */}
            <div className="text-[#6D778E] text-[14px] leading-[20px] font-[400px] w-full mt-[10px] h-[62px] flex flex-col justify-start items-start">
              <div>Geslo</div>

              <div className="px-[10px] mt-[4px] h-[38px] rounded-[6px] bg-[#F2F8FF66] shadow-custom-dark-to-white w-full">
                <input
                  type="password"
                  value={inputValueGeslo}
                  autoComplete={activeDiv === "login" ? "current-password" : "new-password"}
                  onChange={handleGesloInput}
                  className="h-[38px] w-full bg-transparent focus:outline-none text-[#848484]"
                />
              </div>
            </div>

            {activeDiv === "register" && (
              <div className="text-[#6D778E] text-[14px] leading-[20px] font-[400px] w-full mt-[10px] min-h-[62px] flex flex-col justify-start items-start">
                <div>Ponovi geslo</div>
                <div className="px-[10px] mt-[4px] h-[38px] rounded-[6px] bg-[#F2F8FF66] shadow-custom-dark-to-white w-full">
                  <input
                    type="password"
                    value={inputValueConfirmGeslo}
                    autoComplete="new-password"
                    onChange={(e) => setInputValueConfirmGeslo(e.target.value)}
                    className="h-[38px] w-full bg-transparent focus:outline-none text-[#848484]"
                  />
                </div>
                {passwordsDiffer && (
                  <p role="alert" className="mt-[2px] text-[12px] leading-[14px] text-[#C62828]">
                    Gesli se ne ujemata
                  </p>
                )}
              </div>
            )}

            {/* Container for checkboxes */}
            <div className=" flex flex-col items-center mt-[25px]">
              {/* Container for forgot password and remember me */}
              {activeDiv === "login" && (
                <div className="flex w-full justify-between items-center mt-[-9px]">
                  <label className="flex flex-1 justify-start mt-[3px] ml-[2px] font-custom400 text-[12px] text-[#6D778E]">
                    <Checkbox
                      checked={enabledRememberMe}
                      onChange={setEnabledRememberMe}
                      className="mr-[12px] justify-center items-center group w-[18px] h-[18px] rounded-[2px] border-[2px] border-[#6D778E] bg-white/10 ring-white/15 ring-inset data-[checked]:bg-[#6D778E]"
                    >
                      <CheckIcon className="hidden ml-[-1px] size-4 rotate-6 fill-white group-data-[checked]:block" />
                    </Checkbox>
                    Zapomni si
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="flex font-custom400 mt-[3px] text-[12px] leading-[20px] font-variation-customOpt12 text-[#6D778E] justify-end "
                  >
                    Pozabljeno geslo
                  </button>
                </div>
              )}
              {/* Checkbox for receive emails*/}
              {activeDiv === "register" && (
                <label className="flex flex-1 justify-start mobile:w-[] mt-[3px] ml-[2px] font-variation-customOpt12 leading-[14px] font-custom400 text-[12px] text-[#6D778E]">
                  <Checkbox
                    checked={enableReceiveEmails}
                    onChange={setEnableReceiveEmails}
                    className="mr-[12px] justify-center items-center group min-w-[18px] h-[18px] rounded-[2px] border-[2px] border-[#6D778E] bg-white/10 ring-white/15 ring-inset data-[checked]:bg-[#6D778E]"
                  >
                    <CheckIcon className="hidden ml-[-1px] size-4 rotate-6 fill-white group-data-[checked]:block" />
                  </Checkbox>
                  <div className="">
                    Dajem privolitev, da me Osmrtnica.com preko elektronske pošte,
                    do preklica obvešča o posodobitvah, nadgradnjah, novostih in
                    ugodnostih.
                  </div>
                </label>
              )}

              {/* Checkbox for terms of use and privacy policy*/}
              {activeDiv === "register" && (
                <div className=" flex w-full items-center mt-[7px]">
                  {/* Checkbox */}
                  <label className="flex flex-1 justify-start mt-[3px] ml-[2px] font-custom400 text-[12px] text-[#6D778E]">
                    <Checkbox
                      checked={enableTermsOfUse}
                      onChange={setEnableTermsOfUse}
                      className="mr-[12px] justify-center items-center group w-[18px] h-[18px] rounded-[2px] border-[2px] border-[#6D778E] bg-white/10 ring-white/15 ring-inset data-[checked]:bg-[#6D778E]"
                    >
                      <CheckIcon className="hidden ml-[-1px] size-4 rotate-6 fill-white group-data-[checked]:block" />
                    </Checkbox>
                    {/* One text, the two links inside it (as plain links, not as separate boxes). */}
                    <span>
                      Strinjam se s{" "}
                      <Link href={"/splosni-pogoji"} className="underline">
                        splošnimi pogoji
                      </Link>{" "}
                      in{" "}
                      <Link href={"/politika-zasebnosti"} className="underline">
                        politiko zasebnosti
                      </Link>
                    </span>
                  </label>
                  {/*  */}
                </div>
              )}
            </div>
          </div>

          {activeDiv === "register" && (
            <div className=" w-[429px] mobile:w-[297px] h-[48px] mt-[20px] mx-auto shadow-custom-light-dark-with-white">
              <button
                type="button"
                onClick={handleRegister}
                disabled={isRegistering || passwordsDiffer}
                className="flex w-full h-full items-center justify-center text-[16px] leading-[24px] font-variation-customOpt16 text-[#FFFFFF] bg-gradient-to-b from-[#0D94E8] to-[#1860A3] rounded-lg disabled:opacity-60"
              >
                {isRegistering ? "Registriram…" : "Registriraj se"}
              </button>
            </div>
          )}

          {activeDiv === "login" && (
            <div className="w-[429px] mobile:w-[297px] h-[48px] mt-[20px] mx-auto shadow-custom-light-dark-with-white">
              <button
                type="button"
                onClick={handleLogin}
                className="flex w-full h-full bg-white items-center justify-center text-[16px] leading-[24px] font-variation-customOpt16 text-[#FFFFFF] bg-gradient-to-b from-[#0D94E8] to-[#1860A3] rounded-lg"
              >
                Prijavi se
              </button>
            </div>
          )}

          <div className="h-[48px] mx-auto flex justify-center items-end">
            <div className="text-[14px] font-[400px] text-[#414141]">
              Ali hitra prijava preko
            </div>
          </div>
          <div className="w-[429px] mobile:w-[297px] mx-auto mt-[16px] h-[52px] mobile:h-auto mobile:gap-[13px] flex flex-row mobile:flex-col justify-between items-center">
            <button
              type="button"
              onClick={() => handleSocialLogin("facebook", "Facebook")}
              // Facebook login is not offered on phones (tablet and desktop only).
              className="mobile:hidden w-[200px] mobile:w-full h-[52px] mobile:py-[12px] shadow-custom-dark-to-white bg-[#E7EBF0] border-[#FFFFFF40] rounded-[10px] border-[0.5px] flex items-center justify-center"
            >
              <Image
                src={"/ico_fb.png"}
                width={28}
                height={28}
                alt="Facebook Icon"
              />
              <div className="ml-[8px] text-[16px] leading-[100%] font-variation-customOpt16 text-[#414141]">
                Facebook
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleSocialLogin("google", "Google")}
              className="w-[200px] mobile:w-full h-[52px] mobile:py-[12px] shadow-custom-dark-to-white bg-[#E7EBF0] border-[#FFFFFF40] border-[0.5px] rounded-[10px] flex items-center justify-center"
            >
              <Image
                src={"/ico_google.png"}
                width={28}
                height={28}
                alt="Google Icon"
              />
              <div className="ml-[8px] text-[16px] leading-[100%] font-variation-customOpt16 text-[#414141]">
                Google
              </div>
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default Registration;
